import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface UseTabbedNavOptions<T extends string> {
  baseURL: string;
  defaultKey: T;
  navs?: T[];
}

export type GoToTab<T extends string = string> = (tab: T, params?: Record<string, string>) => void;

export const buildTabPath = (
  basePath: string,
  tab: string,
  params?: Record<string, string>
): string => {
  const search = params ? new URLSearchParams(params).toString() : '';
  return `${basePath}/${tab}${search ? `?${search}` : ''}`;
};

export const useTabbedNav = <T extends string>({
  baseURL,
  defaultKey,
  navs = [defaultKey]
}: UseTabbedNavOptions<T>): { tab: T; goToTab: GoToTab<T> } => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const normalizedBase = baseURL.replace(/\/$/, '');
  const baseURLIndex = pathname.indexOf(normalizedBase);
  const basePath =
    baseURLIndex === -1 ? '' : pathname.slice(0, baseURLIndex + normalizedBase.length);
  const afterBase =
    baseURLIndex === -1 ? '' : pathname.slice(baseURLIndex + normalizedBase.length + 1);
  const hasTab = afterBase.length > 0 && afterBase !== '/';
  // Only the first path segment is the tab key -- a tab whose own <Routes> renders further
  // sub-routes (e.g. HROAS's `budget-optimizations/*` -> `runs/:id`) would otherwise never
  // match `navs` (which only ever lists bare tab keys), and get redirected to defaultKey on
  // every navigation into one of those sub-routes.
  const firstSegment = hasTab ? afterBase.split('/')[0] : '';
  const isValidTab = hasTab && (navs.length <= 1 || (navs as string[]).includes(firstSegment));
  const tab = (isValidTab ? firstSegment : defaultKey) as T;

  useEffect(() => {
    if (hasTab && !isValidTab) {
      navigate(`${basePath}/${defaultKey}`, { replace: true });
    }
  }, [hasTab, isValidTab, defaultKey, navigate, basePath]);

  const goToTab = useCallback(
    (newTab: T, params?: Record<string, string>) =>
      navigate(buildTabPath(basePath, newTab, params)),
    [navigate, basePath]
  );

  return { tab, goToTab };
};

const WILDCARD = '*';

interface NestedRoute {
  key?: string;
  label?: string;
  uri?: string;
  depth?: number;
  subRoutes?: NestedRoute[];
}

interface ResolvedPathNode {
  key?: string;
  label?: string;
  uri?: string;
  resolvedPath?: string;
}

const findPath = (route: NestedRoute, pathParts: string[]): ResolvedPathNode[] | null => {
  if (pathParts.length === 0 || !route.subRoutes?.length) {
    return [];
  }

  const [head] = pathParts;
  for (const subRoute of route.subRoutes) {
    if (subRoute.uri !== WILDCARD && subRoute.uri !== head) {
      continue;
    }
    const res = findPath(subRoute, pathParts.slice(1 + (subRoute.depth ?? 0)));
    if (res === null) {
      return null;
    }
    const elem: ResolvedPathNode = {
      key: subRoute.key,
      label: subRoute.label,
      uri: subRoute.uri
    };
    if (subRoute.uri === WILDCARD) {
      elem.resolvedPath = head;
    }
    if (subRoute.label === WILDCARD) {
      elem.label = head;
    }
    if (subRoute.depth) {
      elem.resolvedPath = pathParts.slice(0, subRoute.depth + 1).join('/');
    }
    return [elem, ...res];
  }
  return null;
};

interface UsePageTitleNavOptions {
  baseURL: string;
  routes: NestedRoute[];
}

interface PageTitleNav {
  /** Absolute prefix the breadcrumb anchors to, e.g. `/acme/streaming/buying`. */
  basePath: string;
  path: ResolvedPathNode[];
}

/**
 * Resolves the current URL against a tree of nested routes for breadcrumb-style page titles.
 * Pass the returned `basePath` and `path` to `BreadcrumbTitle`; it joins them into absolute
 * crumb URLs, so navigation stays correct no matter how deep the current URL is. For plain
 * top-level tabs, use `useTabbedNav` instead.
 */
export const usePageTitleNav = ({ baseURL, routes }: UsePageTitleNavOptions): PageTitleNav => {
  const { pathname } = useLocation();

  return useMemo(() => {
    const match = pathname.match(new RegExp(`^(/[^/]+/${baseURL})/?(.*)$`));
    if (!match) {
      return { basePath: '', path: [] };
    }
    const [, basePath, rest] = match;
    const pathParts = rest.split('/');
    return { basePath, path: findPath({ subRoutes: routes }, pathParts) ?? [] };
  }, [pathname, baseURL, routes]);
};
